import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import SeoMetaProps from '../../../models/SeoMeta';
import formatLink from '../../../libs/formatters/format-link';
import replaceACFVariables from '../../../libs/utils/replace-acf-variables';
import LanguageContext from '../../../context/language';
import { useOptions } from '../../../context/options';
import replaceHostName from '../../utils/replace-host-name';
import { token } from '../../../js/localizations/current-locale';

const PureSeo = ({
  siteMeta,
  seo,
  options,
  location,
  translations,
  acfGroups,
}) => {
  const { lang } = useContext(LanguageContext);
  const {
    noFollow,
    noIndex,
  } = seo;
  const robotsString = `${noFollow || ''}${(noFollow && noIndex) ? ', ' : ''}${noIndex || ''}`;
  const {
    logo = {
      link: {},
      image: {},
    },
  } = options.header;
  const {
    companyName,
    postAddress,
    phoneNumber,
    email,
  } = options.variables;
  // Expand BR script with:
  // replace with current customer ID or leave commented out for an anonymous customer
  // customer: window.currentUserId,
  // Experimental was not added directly due to potential compliance conflict
  // experimental: {
  //   non_personalized_weblayers: true
  // },
  const bloomreachScript = `!function(e,n,t,i,r,o){function s(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var a=4e3,c="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;var r=t.getElementsByTagName(n)[0];return r.parentNode.insertBefore(i,r),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}o.target=o.target||"https://api.exponea.com",o.file_path=o.file_path||o.target+"/js/exponea.min.js",r[n]=p(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize","_initializeConfig"]),r[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),r[n].segments=p(["subscribe"]),r[n]["snippetVersion"]="v2.7.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(r,n,"performance"),function(e,n,t,i,r,o){e[r]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(o.exec(n.cookie)||["","new"])[1],path:t.target}}(r,e,o,n,i,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(o,t,e),function(e,n,t,i,r,o,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var l,f=e.new_experiments.hide_class||c,_=e.new_experiments.timeout||a,g=encodeURIComponent(o.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?l=s(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(l=s(e.cookies.expires.tracking))),l&&l<new Date&&(l=void 0);var d=e.target+"/webxp/"+n+"/"+o[t].sign+"/modifications.min.js?http-referer="+g+"&timeout="+_+"ms"+(l?"&cookie-expires="+Math.floor(l.getTime()/1e3):"");"sync"===e.new_experiments.mode&&o.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,r){t[r][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[r][n]),i.writeln("<"+n+">!"+r+".init && document.writeln("+r+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(d,n,o,p,t):function(e,n,t,i,r,o,s,a){o.documentElement.classList.add(e);var c=m(t,i,o);function p(){r[a].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,o)}function u(){o.documentElement.classList.remove(e)}c.onload=p,c.onerror=p,r.setTimeout(u,n),r[s]._revealPage=u}(f,_,d,n,o,p,r,t)}}(o,t,i,0,n,r,e),function(e,n,t){var i;e[n]._initializeConfig(t),(null===(i=t.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[n]._preInitialize(t),e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(r,n,o)}(document,"exponea","script","webxpClient",window,{
    target: "https://api.eu1.exponea.com",
    token: "${token}",
    track: {
        visits: true,
    },
    });
    exponea.start();`;

  useEffect(() => {
    if (typeof window !== 'undefined' && token !== null && window.self === window.parent) {
      const handleConsent = () => {
        if (window.Cookiebot?.consent?.marketing) {
          const existing = document.querySelector('script[data-bloomreach="true"]');
          if (!existing) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.setAttribute('data-bloomreach', 'true');
            script.innerHTML = bloomreachScript;
            document.body.appendChild(script);
          }
        }
        return undefined;
      };
      handleConsent();
      window.addEventListener('CookiebotOnAccept', handleConsent);
      return () => {
        window.removeEventListener('CookiebotOnAccept', handleConsent);
      };
    }
    return undefined;
  }, [bloomreachScript]);

  const imageUrl = ({ image }) => {
    if (image.svgImage) {
      return replaceHostName(
        image.svgImage?.node?.sourceUrl,
        process.env.GATSBY_FRONTEND_BASE_URL,
      );
    }
    if (image.image) {
      return `${process.env.GATSBY_FRONTEND_BASE_URL}${image.image.node.gatsbyImage.images.fallback.src}`;
    }
    return '';
  };

  const isStartpage = location.pathname === '/';

  const orgStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: process.env.GATSBY_FRONTEND_BASE_URL,
    logo: imageUrl(logo),
    name: companyName,
    telephone: phoneNumber,
    email: `mailto:${email}`,
    address: postAddress ? {
      '@type': 'PostalAddress',
      streetAddress: postAddress?.box,
      postalCode: postAddress?.zip,
      addressLocality: postAddress?.city,
      addressCountry: process.env.REACT_APP_LOCALE,
    } : null,
    areaServed: process.env.REACT_APP_LOCALE,
  };

  const siteTitle = seo
    ? seo.title
    : `${siteMeta && siteMeta.description} - ${siteMeta && siteMeta.name}`;

  const hasTranslatedPages = translations.length > 0;

  // This is a temporary const to handle specific SEO requirement
  // const hreflangGroup = acfGroups?.find((group) =>
  //  group.__typename === 'WpAcfSectionsHreflang')?.hreflangGroup || null;

  let hreflangGroup = null;
  if (acfGroups) {
    const foundGroup = acfGroups.find((group) => group.__typename === 'WpAcfSectionsHreflang');
    if (foundGroup) {
      hreflangGroup = foundGroup.hreflangGroup;
    }
  }

  return (
    <Helmet>
      <html
        scroll-behavior="smooth" // eslint-disable-line react/no-unknown-property
        lang={lang}
      />
      <link rel="icon" type="image/png" href={seo.companyLogo} />
      {hasTranslatedPages && translations.map((language, key) => (
        <link
          key={`translated-page-${key}`}
          hrefLang={language.locale}
          href={language.href && language.href.replace('/home', '')}
          rel="alternate"
        />
      ))}
      {hasTranslatedPages && (
        <link
          hrefLang={lang}
          href={location.href}
          rel="alternate"
        />
      )}
      {hreflangGroup?.map(({ language, page }) => (
        <link
          key={language}
          hrefLang={language}
          href={page}
          rel="alternate"
        />
      ))}
      {robotsString && (
        <meta name="robots" content={robotsString} />
      )}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>{replaceACFVariables(siteTitle, options.variables)}</title>
      <meta
        property="og:title"
        content={seo && replaceACFVariables(
          seo.openGraphTitle || siteTitle,
          options.variables)}
      />
      <meta
        name="twitter:title"
        content={seo && replaceACFVariables(
          seo.twitterTitle || siteTitle,
          options.variables)}
      />
      <meta
        property="og:site_name"
        content={seo && replaceACFVariables(
          seo.title, options.variables)}
      />
      <meta
        name="description"
        content={seo && replaceACFVariables(
          seo.description, options.variables)}
      />
      <meta
        property="og:description"
        content={seo && replaceACFVariables(
          seo.openGraphDescription || seo.description,
          options.variables)}
      />
      <meta
        name="twitter:description"
        content={seo && replaceACFVariables(
          seo.twitterDescription || seo.description,
          options.variables)}
      />
      <meta
        property="og:image"
        // eslint-disable-next-line camelcase
        content={seo && seo.socialImage?.url}
      />
      <meta
        name="twitter:image"
        // eslint-disable-next-line camelcase
        content={seo && seo.socialImage?.url}
      />
      <link
        rel="canonical"
        href={seo && formatLink(seo.canonicalUrl, null, true)}
      />
      <meta
        property="og:url"
        content={seo && formatLink(
          seo.canonicalUrl, null, true)}
      />
      <link
        rel="shortcut icon"
        type="image/png"
        // eslint-disable-next-line camelcase
        href={seo && seo.socialImage?.url}
      />
      <link
        rel="preconnect"
        href="https://www.googletagmanager.com/"
      />
      <link
        rel="preconnect"
        href="https://static.hotjar.com/"
      />
      <link
        rel="preconnect"
        href="https://script.hotjar.com/"
      />
      <link
        rel="preconnect"
        href="https://vc.hotjar.io/"
      />
      <link
        rel="preconnect"
        href="https://pro.ip-api.com/"
      />
      <link
        rel="preconnect"
        href="https://bat.bing.com/"
      />
      <link
        rel="preconnect"
        href="https://adtr.io/"
      />
      <meta property="og:type" content="website" />
      <meta
        name="twitter:card"
        content="summary_large_image"
      />
      {isStartpage && (
        <script type="application/ld+json">{`${JSON.stringify(orgStructuredData)}`}</script>
      )}
    </Helmet>
  );
};

PureSeo.propTypes = {
  seo: SeoMetaProps,
  siteMeta: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  options: PropTypes.shape(),
  location: PropTypes.shape(),
  translations: PropTypes.arrayOf(PropTypes.shape()),
  acfGroups: PropTypes.arrayOf(
    PropTypes.shape({
      __typename: PropTypes.string.isRequired,
    }),
  ),
};

PureSeo.defaultProps = {
  seo: {},
  siteMeta: {},
  options: {},
  location: {},
  translations: [{}],
  acfGroups: [{}],
};

const Seo = (props) => {
  const { optionVariables, optionHeader } = useOptions();
  return (
    <PureSeo
      {...props}
      options={{
        variables: optionVariables,
        header: optionHeader,
      }}
    />
  );
};

export { PureSeo };
export default Seo;
